import { t } from "@/i18n-js/instance";
import {
  dateFormats,
  formattedDateTime,
} from "@/react/helpers/dateTimeHelpers/formattedDateTime";
import type { QuizAttempt } from "@/react/types/QuizAttempt";
import { Form } from "@circle-react-uikit/Form";
import { Typography } from "@circle-react-uikit/Typography";
import { UserImage } from "@circle-react-uikit/UserImage";
import type { QuizCommunityMember } from "../../QuizResults";
import { ResultCell } from "../ResultCell";

interface QuizResultSummaryProps {
  attempt: QuizAttempt;
  resultValue: "pending" | "passed" | "failed";
  student: QuizCommunityMember;
}

export const QuizResultSummary = ({
  attempt,
  student,
  resultValue,
}: QuizResultSummaryProps) => {
  const translationRoot = "courses.admin_summary.engagement.quiz_results";
  const selectOptions = ["passed", "failed"].map(option => ({
    label: t([translationRoot, `results.${option}`]),
    value: option,
  }));
  if (resultValue === "pending") {
    selectOptions.unshift({
      label: t([translationRoot, "select_grade"]),
      value: "pending",
    });
  }
  return (
    <div className="border-primary mb-6 flex w-full shrink-0 flex-col divide-y rounded-md border p-8 sm:mb-0 sm:w-80">
      <div className="flex justify-between gap-4 pb-5">
        <div className="flex flex-col">
          <Typography.LabelSm weight="semibold">
            {student.name}
          </Typography.LabelSm>
          <Typography.LabelSm weight="normal" color="text-default">
            {t("courses.admin_summary.engagement.quiz_results.attempts")}
            {": "}
            {attempt.quiz_attempts_count}
          </Typography.LabelSm>
        </div>
        <UserImage
          src={student.avatar_url}
          name={student.name}
          size="8"
          isFullRounded
        />
      </div>
      <div className="flex flex-col py-5">
        <Typography.LabelSm weight="semibold">
          {attempt.lesson_name}
        </Typography.LabelSm>
        <Typography.LabelSm weight="normal" color="text-default">
          {attempt.section_name}
        </Typography.LabelSm>
      </div>
      <div className="flex flex-col py-5">
        <Typography.LabelSm weight="semibold">
          {t("courses.admin_summary.engagement.quiz_results.submitted")}
        </Typography.LabelSm>
        <Typography.LabelSm weight="normal" color="text-default">
          {formattedDateTime({
            dateTime: new Date(attempt.created_at),
            format: dateFormats.short_date_at_short_time,
          })}
        </Typography.LabelSm>
      </div>
      <div className="flex py-5">
        <div className="flex w-1/2 flex-col gap-2">
          <Typography.LabelSm weight="semibold">
            {t("courses.admin_summary.engagement.quiz_results.score")}
          </Typography.LabelSm>
          <Typography.TitleXl>{attempt.grade}%</Typography.TitleXl>
        </div>
        <div className="flex w-1/2 flex-col gap-2">
          <Typography.LabelSm weight="semibold">
            {t("courses.admin_summary.engagement.quiz_results.correct")}
          </Typography.LabelSm>
          <Typography.TitleXl>
            {t("courses.view_lesson.quiz_result.correct_answers_count", {
              correct: attempt.correct_responses,
              total: attempt.questions.length,
            })}
          </Typography.TitleXl>
        </div>
      </div>
      <div className="flex flex-col gap-3 py-5">
        <Typography.LabelSm weight="semibold">
          {t(
            "courses.admin_summary.engagement.quiz_results.did_student_pass_or_fail",
          )}
        </Typography.LabelSm>
        {attempt.passing_grade !== null &&
        attempt.passing_grade !== undefined ? (
          <ResultCell attempt={attempt} />
        ) : (
          <Form.Item
            name="result"
            className="!pt-0"
            hideLabel
            hideDescription
            hideBorder
          >
            <Form.SelectNative name="result" options={selectOptions} />
          </Form.Item>
        )}
      </div>
    </div>
  );
};
